// import { MenuItemIdGenerator } from "../../util/avantoeats/menuItemIdGenerator/MenuItemIdGenerator";
// import { AvantoCondition } from "../conditions/AvantoCondition";
import { Customization } from "./Customization";
import { CartItem } from "../cart/CartItem";
import { CartItemCustomizations } from "../cart/CartItemCustomizations";
import BigNumber from "bignumber.js";
import { DisplayOptions } from "./DisplayOptions";
import { ShouldElementBeDisplayed } from "./ShouldElementBeDisplayed";
import { CurrentDateTimeInfo } from "./CurrentDateTimeInfo";

/**
 * Defines a product of the menu, which is an item the customer can buy
 * For example: "Big Mac" would be a product
 */
export class Product {
    static readonly ID_PREFIX = "prod";

    client: string;
    id: string;
    title: string;
    parentCategoryId: string;
    description: string;
    img: string;
    active: boolean;
    unavailable:boolean;
    unavailableMsg:string;
    displayOrder: number;
    price: number;
    deliveryPrice: number;
    version: number | null;
    customizations: Map<string, Customization>;//Map containing the customization ID and it's values
    parentCategoryDisplayOptions:DisplayOptions | null;
    displayOptions:DisplayOptions | null;
    allowQttyEdit:boolean;

    //The following is NOT saved on AWS. We calculate it whenever we load data from the server and we know the server time.
    shouldDisplay:ShouldElementBeDisplayed | null;

    /**
     * Constructor
     * @param client PARTITION KEY: On the format "region.restaurantName". Unique identifier that tells us which restaurant this product belongs to
     * @param id SORT KEY: Unique identifier of this object on the database. Will be on the format "prod.XXXXXX", where XXXXX is the hexadecimal timestamp of when this product was created
     * @param title Product title (ex: big mac)
     * @param parentCategoryId ID of the category this product is part of (ex: Burgers)
     * @param description Product description (ex: 2 all beef patties, special sauce, lettuce, cheese, pickles, onions and a sesame bun)
     * @param img path to the image
     * @param active Inactive products won't be shown on the restaurant menu page
     * @param unavailable Unavailable producsts will be displayed, but won't be selectable by the user
     * @param unavailableMsg Message describing why the product is unabailable
     * @param order Display order of this product (1 will be the first shown, 2 the second...)
     * @param price default price of this product
     * @param deliveryPrice delivery price of this product
     * @param version: the version of the product. Changes every time it's updated
     * @param customizations A product may have a couple of customizations related to it...
     * @param parentDisplayOptions Options determining whether the parent category of this product should be displayed for the customer or not
     * @param displayOptions Options determining whether this product should be displayed for the customer or not
     * @param allowQttyEdit If false, you wont be able to edit the quantity of this product using the "-" and "+" buttons at the bottom of the modal that shows up when you click a product
     * @param serverDateTime (OPTIONAL) If present, we use it to calculate whether this product should be displayed or not, 
     * based on it's display options. If not present, we assume that the category must always be displayed.
     */
    constructor(
        client: string, id: string, title: string, description: string, img: string, active: boolean, unavailable:boolean, unavailableMsg:string,  order: number,
        parentCategoryId: string, price: number, delPrice: number, version: number | null, customizations: Map<string, Customization>, 
        parentCategoryDisplayOptions:DisplayOptions | null, displayOptions:DisplayOptions | null, allowQttyEdit:boolean|null,
        serverCurrentTimeInfo?:CurrentDateTimeInfo) {

        this.client = client;
        this.id = id;
        this.title = title;
        this.parentCategoryId = parentCategoryId;
        this.description = description;
        this.img = img;
        this.active = active;
        this.unavailable = unavailable;
        this.unavailableMsg = unavailableMsg;
        this.displayOrder = order;
        this.price = price;
        this.deliveryPrice = delPrice;
        this.version = version;
        this.customizations = customizations;
        this.parentCategoryDisplayOptions = parentCategoryDisplayOptions;
        this.displayOptions = displayOptions;
        this.allowQttyEdit = allowQttyEdit==null?true:allowQttyEdit;
        if(serverCurrentTimeInfo!=null && displayOptions!=null){
            this.shouldDisplay = displayOptions.determineDisplayConditions(serverCurrentTimeInfo);
        }else{
            this.shouldDisplay = null;
        }
    }

    getIdString(){
        return this.client+"."+this.id;
    }

    calculateSelectedPrice(
        productQuantitySelected: number,
        selectedCustomizations: {
            customizationId: string,
            selectedOptions: {
                optionId: string,
                quantity: number,
                itemMultiplier:number, 
                toppingSide:"left"|"full"|"right"|null
            }[]
        }[],
        isDelivery: boolean
    ): number {
        let selectedCustomizationsTotalPrice = new BigNumber(0);
        if (selectedCustomizations != null) {
            selectedCustomizations.forEach(selected => {
                selectedCustomizationsTotalPrice = selectedCustomizationsTotalPrice.plus(this.customizations.get(selected.customizationId)!.calculateSelectionPrice(selected.selectedOptions, isDelivery));
            });
        }
        const productPrice = isDelivery ? this.deliveryPrice : this.price;
        return this.getCalculatedCost(productQuantitySelected, productPrice, selectedCustomizationsTotalPrice);
    }

    /**
     * Generates a CartItem object based on this product
     * @param cartItemId The ID the cartItem will have
     * @param selectedQtty How many of this product have been selected
     * @param productNote Additional note set for this product
     * @param selectedCustomizations Which customizations of this product were selected 
     */
    toCartItem(
        cartItemId: string,
        selectedQtty: number,
        productNote: string,
        selectedCustomizations: {
            customizationId: string,
            selectedOptions: {
                optionId: string,
                quantity: number,
                itemMultiplier:number, 
                toppingSide:"left"|"full"|"right"|null
            }[]
        }[]
    ): CartItem {
        const cartCustomizations: CartItemCustomizations[] = [];
        let customizationsPrice = new BigNumber(0); //This will store the total price of the selected customizations
        let customizationsDeliveryPrice = new BigNumber(0);//This will store the total price of the selected DELIVERY customizations

        //If there are selected customizations, we calculate their prices
        if (selectedCustomizations != null && selectedCustomizations.length > 0) {
            selectedCustomizations.forEach(element => {
                const selectedCustomization = this.customizations.get(element.customizationId);
                const selectionInfo = selectedCustomization!.getSelectionInformation(element.selectedOptions);
                cartCustomizations.push(new CartItemCustomizations(
                    element.customizationId,
                    selectedCustomization!.title,
                    selectionInfo.price,
                    selectionInfo.deliveryPrice,
                    selectionInfo.selectionString,
                    element.selectedOptions
                ));
                customizationsPrice =  customizationsPrice.plus(selectionInfo.price);
                customizationsDeliveryPrice = customizationsDeliveryPrice.plus(selectionInfo.deliveryPrice);
            });           
        }

        return new CartItem(
            cartItemId,
            this.id,
            this.version,
            this.title,
            this.getCalculatedCost(selectedQtty, this.price, customizationsPrice),
            this.getCalculatedCost(selectedQtty, this.deliveryPrice, customizationsDeliveryPrice),
            selectedQtty,
            cartCustomizations,
            productNote,
            this.parentCategoryDisplayOptions, 
            this.displayOptions
        );
    }

    getCalculatedCost(productQuantitySelected:number, productPrice:number, customizationsTotal:number | BigNumber) {
        //Quantity * (productPrice + customizationsTotal)
        return new BigNumber(productQuantitySelected).times(new BigNumber(productPrice).plus(customizationsTotal)).decimalPlaces(2).toNumber()
    }


     /** Creates a cateogry object from the data pulled from AWS. Some attributes will be added later. */
     static newFromAWS(awsItem: any, serverDateTime?:any) {
        const customizationMap: Map<string, Customization> = new Map();
        if (awsItem.customizations != null) {
            Object.keys(awsItem.customizations).forEach(key => {
                customizationMap.set(key, Customization.newFromAWS(awsItem.customizations[key]));
            });
        }

        const newProduct = new Product(
            awsItem.client,
            awsItem.id,
            awsItem.title,
            awsItem.description,
            awsItem.img,
            awsItem.active,
            awsItem.unavailable,
            awsItem.unavailableMsg,
            awsItem.displayOrder,
            awsItem.parentCategoryId,
            awsItem.price,
            awsItem.deliveryPrice != null ? awsItem.deliveryPrice : awsItem.price, //If there is no delivery price, we use the pickup price,
            awsItem.version,
            customizationMap,
            DisplayOptions.newFromAWS(awsItem.parentDisplayOptions),
            DisplayOptions.newFromAWS(awsItem.displayOptions),
            awsItem.allowQttyEdit,
            serverDateTime
        );

        return newProduct;
    }
}